// src/pages/DashboardPage.js — iOS SAFE + ErrorBoundary protected
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import { useLang } from '../lib/i18n'
import ErrorBoundary from '../components/ErrorBoundary'
import Navbar from '../components/Navbar'
import SocialFooter from '../components/SocialFooter'
import MemberCard from '../components/MemberCard'
import GoldChat from '../components/GoldChat'
import PremiumChat from '../components/PremiumChat'
import VoiceCall from '../components/VoiceCall'
import DinnerBooking from '../components/DinnerBooking'
import ContactsPanel from '../components/ContactsPanel'
import PresenceIndicator from '../components/PresenceIndicator'
import CountdownBanner from '../components/CountdownBanner'
import VaultGallery from '../components/VaultGallery'
import UpgradeCelebration from '../components/UpgradeCelebration'
import { TIER_RANK, TIER_LABEL } from '../lib/tierFeatures'
import WeeklyQuestion from '../components/WeeklyQuestion'
import LoyaltyStreak from '../components/LoyaltyStreak'
import ReferralPanel from '../components/ReferralPanel'
import BirthdayBanner from '../components/BirthdayBanner'
import NotificationPrompt from '../components/NotificationPrompt'
import InstallPrompt from '../components/InstallPrompt'
import AskMeAnything from '../components/AskMeAnything'
import MemberProfile from '../components/MemberProfile'
import './DashboardPage.css'

const TABS_BY_TIER = {
  gold:     ['overview','chat','ama','vault','contacts','referral','profile'],
  premium:  ['overview','chat','ama','vault','contacts','referral','profile'],
  platinum: ['overview','chat','voice','dinner','ama','vault','contacts','referral','profile'],
}

export default function DashboardPage() {
  const { session, profile } = useAuth()
  const { t } = useLang()
  const toast = useToast()
  const tier  = profile?.tier || 'gold'
  const tabs  = TABS_BY_TIER[tier] || TABS_BY_TIER['gold']
  const [activeTab, setActiveTab] = useState('overview')
  const [chatUnread, setChatUnread] = useState(0)
  const [amaUnread,  setAmaUnread]  = useState(0)
  const prevTierRef = useRef(null)
  const [celebration, setCelebration] = useState(null) // { from, to }

  useEffect(() => {
    if (activeTab === 'chat') setChatUnread(0)
    if (activeTab === 'ama')  setAmaUnread(0)
  }, [activeTab])

  // The moment a member's tier changes (admin updates it live), celebrate an
  // upgrade or quietly notify on a downgrade. The last-seen tier is also kept
  // in localStorage so a member upgraded while offline still gets the moment
  // next time they open the app.
  useEffect(() => {
    if (!profile?.tier || !session?.user?.id) return
    const key = `skybri-last-tier-${session.user.id}`
    let stored = null
    try { stored = localStorage.getItem(key) } catch (e) {}
    const previous = prevTierRef.current || stored
    if (previous && TIER_RANK[previous] && previous !== profile.tier) {
      if (TIER_RANK[profile.tier] > TIER_RANK[previous]) {
        setCelebration({ from: previous, to: profile.tier })
      } else {
        toast.show(`Your membership is now ${TIER_LABEL[profile.tier] || profile.tier}`, 'gold')
      }
    }
    prevTierRef.current = profile.tier
    try { localStorage.setItem(key, profile.tier) } catch (e) {}
  }, [profile?.tier, session?.user?.id]) // eslint-disable-line

  const tierName = { gold:'Gold', premium:'Premium', platinum:'Platinum' }[tier] || 'Gold'
  const tierSub  = {
    gold:     t('theAdmirer')      || 'The Admirer',
    premium:  t('theCompanion')    || 'The Companion',
    platinum: t('theIntimateCircle') || 'The Intimate Circle'
  }[tier] || 'The Admirer'

  const TAB_LABELS = {
    overview:'Overview', chat:'Message', voice:'Voice Call', dinner:'Dinner',
    ama:'Ask Me Anything', vault:'Vault', contacts:'Contacts',
    referral:'Refer & Earn', profile:'My Profile'
  }

  return (
    <div className="dashboard" style={{minHeight:'100vh',background:'var(--bg)'}}>
      <Navbar chatUnread={chatUnread} amaUnread={amaUnread} />

      {celebration && (
        <ErrorBoundary>
          <UpgradeCelebration
            fromTier={celebration.from}
            toTier={celebration.to}
            name={profile?.display_name}
            onClose={() => setCelebration(null)}
          />
        </ErrorBoundary>
      )}

      {/* Risky components wrapped — if they crash iOS, page still shows */}
      <div className="container" style={{paddingTop:12}}>
        <ErrorBoundary><NotificationPrompt userId={session?.user?.id} /></ErrorBoundary>
        <ErrorBoundary><InstallPrompt /></ErrorBoundary>
        <ErrorBoundary><BirthdayBanner profile={profile} tier={tier} /></ErrorBoundary>
      </div>

      {/* Header */}
      <div className="dash-top">
        <div className="container">
          <div className="dash-top__inner">
            <div>
              <p className="dash-top__eyebrow">{t('yourPrivateVault') || 'Your Private Vault'}</p>
              <h1 className="dash-top__greeting">
                {t('welcome') || 'Welcome,'} <em>{profile?.display_name || 'Member'}</em>
              </h1>
              <div style={{marginTop:8}}>
                <ErrorBoundary><PresenceIndicator size="sm" /></ErrorBoundary>
              </div>
            </div>
            <div className={`dash-top__tier-badge dash-top__tier-badge--${tier}`}>
              {profile?.avatar_url && (
                <img src={profile.avatar_url} alt="" style={{width:44,height:44,borderRadius:'50%',objectFit:'cover',border:'1px solid var(--border-mid)',marginBottom:6,display:'block'}} />
              )}
              <span className="dash-top__tier-name">{tierName}</span>
              <span className="dash-top__tier-sub">{tierSub}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="dash-tabs">
        <div className="container">
          <div className="dash-tabs__inner">
            {tabs.map(tab => (
              <button
                key={tab}
                className={`dash-tab ${activeTab===tab?'dash-tab--active':''} dash-tab--${tier}`}
                onClick={() => setActiveTab(tab)}
              >
                {TAB_LABELS[tab]}
                {tab==='chat' && chatUnread>0 && <span className="tab-badge">{chatUnread}</span>}
                {tab==='ama'  && amaUnread>0  && <span className="tab-badge tab-badge--ama">{amaUnread}</span>}
                {(tab==='voice'||tab==='dinner') && <span className="dash-tab__pip">Plat</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container dash-content">

        {activeTab==='overview' && (
          <div className="dash-overview">
            <div className="dash-overview__left">
              <ErrorBoundary><MemberCard profile={profile} /></ErrorBoundary>
              <ErrorBoundary><LoyaltyStreak profile={profile} /></ErrorBoundary>
            </div>
            <div className="dash-overview__right">
              <ErrorBoundary><CountdownBanner /></ErrorBoundary>
              <div className="access-summary">
                <div className="access-summary__title">Your Access — {tierName} Card</div>
                <div className="access-summary__grid">
                  <AccessItem label="Personal Q&A Chat" desc={tier==='gold'?'24hr window · 4x per month':'Unlimited messaging'} active />
                  <AccessItem label="Ask Me Anything" desc={tier==='gold'?'1 question per week':'2 questions per week'} active />
                  <AccessItem label="Exclusive Vault" desc="Private photos & videos" active />
                  <AccessItem label="Weekly Question" desc="Personal reply from Sky Bri" active />
                  <AccessItem label="Zangi Messaging" desc="Private Zangi handle" active={tier!=='gold'} lockNote="Premium & above" locked={tier==='gold'} />
                  <AccessItem label="Private Number" desc="Text directly" active={tier!=='gold'} lockNote="Premium & above" locked={tier==='gold'} />
                  <AccessItem label="Voice Calls" desc="Live audio call" active={tier==='platinum'} lockNote="Platinum only" locked={tier!=='platinum'} />
                  <AccessItem label="Private Dinner" desc="You cover the bill only" active={tier==='platinum'} lockNote="Platinum only" locked={tier!=='platinum'} />
                </div>
              </div>
              {tier==='gold' && (
                <div className="upgrade-nudge">
                  <div>
                    <p className="upgrade-nudge__label">Unlock More</p>
                    <p className="upgrade-nudge__desc">Upgrade to Premium for unlimited chat, Zangi and direct texting.</p>
                  </div>
                  <a href="/join" className="btn btn--gold btn--sm">Upgrade My Card</a>
                </div>
              )}
              {tier==='premium' && (
                <div className="upgrade-nudge upgrade-nudge--plat">
                  <div>
                    <p className="upgrade-nudge__label">Go Platinum</p>
                    <p className="upgrade-nudge__desc">Voice calls and private dinners — the highest level of access.</p>
                  </div>
                  <a href="/join" className="btn btn--plat btn--sm">Upgrade to Platinum</a>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab==='chat' && (
          <div className="dash-section">
            <ErrorBoundary>
              {tier==='gold'
                ? <GoldChat onUnreadChange={setChatUnread} />
                : <PremiumChat tier={tier} onUnreadChange={setChatUnread} />}
            </ErrorBoundary>
          </div>
        )}

        {activeTab==='voice' && tier==='platinum' && (
          <div className="dash-section">
            <ErrorBoundary><VoiceCall profile={profile} /></ErrorBoundary>
          </div>
        )}

        {activeTab==='dinner' && tier==='platinum' && (
          <div className="dash-section">
            <ErrorBoundary><DinnerBooking /></ErrorBoundary>
          </div>
        )}

        {activeTab==='ama' && (
          <div className="dash-section">
            <ErrorBoundary><AskMeAnything tier={tier} onUnread={setAmaUnread} /></ErrorBoundary>
          </div>
        )}

        {activeTab==='vault' && (
          <div className="dash-section dash-section--wide">
            <ErrorBoundary><VaultGallery tier={tier} /></ErrorBoundary>
          </div>
        )}

        {activeTab==='contacts' && (
          <div className="dash-section">
            <ErrorBoundary><ContactsPanel tier={tier} /></ErrorBoundary>
          </div>
        )}

        {activeTab==='referral' && (
          <div className="dash-section">
            <ErrorBoundary><ReferralPanel profile={profile} tier={tier} /></ErrorBoundary>
          </div>
        )}

        {activeTab==='profile' && (
          <div className="dash-section">
            <ErrorBoundary><MemberProfile onSaved={()=>{}} /></ErrorBoundary>
          </div>
        )}

        {activeTab==='weekly' && (
          <div className="dash-section">
            <ErrorBoundary><WeeklyQuestion /></ErrorBoundary>
          </div>
        )}

      </div>
      <SocialFooter />
    </div>
  )
}

function AccessItem({ label, desc, active, locked, lockNote }) {
  return (
    <div className={`access-item ${active?'access-item--active':'access-item--locked'}`}>
      <div className={`access-item__dot ${active?'access-item__dot--on':'access-item__dot--off'}`}/>
      <div className="access-item__text">
        <span className="access-item__label">{label}</span>
        <span className="access-item__desc">{active?desc:lockNote}</span>
      </div>
    </div>
  )
}
